import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Rocket, Loader2, Sparkles, Copy, Trash2, Download, Hash, Tag, Store, CheckCircle2, Circle, Share2, ExternalLink, Palette, RefreshCw, Eye, QrCode, Gamepad2, Code2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

const THEME_SWATCHES = [
  { id: "midnight", label: "Midnight", dot: "#10b981", bg: "#07070b", accent: "#34d399", grad: "radial-gradient(120px 60px at 50% -10%, rgba(16,185,129,0.45), transparent)" },
  { id: "ember", label: "Ember", dot: "#f97316", bg: "#120806", accent: "#fb923c", grad: "radial-gradient(120px 60px at 50% -10%, rgba(249,115,22,0.5), transparent)" },
  { id: "ocean", label: "Ocean", dot: "#22d3ee", bg: "#050b12", accent: "#22d3ee", grad: "radial-gradient(120px 60px at 50% -10%, rgba(34,211,238,0.45), transparent)" },
  { id: "forest", label: "Forest", dot: "#84cc16", bg: "#07110a", accent: "#a3e635", grad: "radial-gradient(120px 60px at 50% -10%, rgba(132,204,22,0.45), transparent)" },
  { id: "candy", label: "Candy", dot: "#ec4899", bg: "#120613", accent: "#f472b6", grad: "radial-gradient(120px 60px at 50% -10%, rgba(236,72,153,0.5), transparent)" },
  { id: "royal", label: "Royal", dot: "#a78bfa", bg: "#0a0716", accent: "#a78bfa", grad: "radial-gradient(120px 60px at 50% -10%, rgba(167,139,250,0.5), transparent)" },
];

function copyText(text, label) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`📋 ${label} copied!`),
    () => toast.error("Couldn't copy to clipboard."),
  );
}

function kitToMarkdown(k) {
  const lines = [];
  lines.push(`# ${k.game_title || "Untitled"}`);
  if (k.tagline) lines.push(`\n> ${k.tagline}`);
  if (k.category) lines.push(`\n**Category:** ${k.category}`);
  if (k.short_desc) lines.push(`\n**Subtitle:** ${k.short_desc}`);
  if (k.long_desc) lines.push(`\n## Description\n\n${k.long_desc}`);
  if ((k.features || []).length) lines.push(`\n## Features\n\n${k.features.map((f) => `- ${f}`).join("\n")}`);
  if ((k.hashtags || []).length) lines.push(`\n## Hashtags\n\n${k.hashtags.map((h) => `#${h}`).join(" ")}`);
  return lines.join("\n");
}

export default function GTMStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kit, setKit] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [game, setGame] = useState(null);
  const [cineCount, setCineCount] = useState(0);
  const [activeEvent, setActiveEvent] = useState(null);
  const [trend, setTrend] = useState([]);
  const [sharing, setSharing] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gtm/${gameId}`);
      setKit(data?.latest || null);
    } catch (e) { toast.error("Couldn't open the Go-to-Market Kit."); }
    finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  // Publish-readiness inputs: the game's real store state + trailer count.
  useEffect(() => {
    (async () => {
      const [g, c] = await Promise.allSettled([
        api.get(`/games/${gameId}`),
        api.get(`/cinematic/${gameId}`),
      ]);
      if (g.status === "fulfilled") setGame(g.value.data);
      if (c.status === "fulfilled") setCineCount((c.value.data?.cinematics || []).length);
      api.get("/events/active").then((r) => setActiveEvent(r.data?.active ? r.data : null)).catch(() => {});
    })();
  }, [gameId]);

  // Poll while the kit is still generating.
  useEffect(() => {
    if (kit?.status === "processing") {
      pollRef.current = setInterval(load, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [kit?.status, load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/gtm/${gameId}/generate`, {}, { timeout: 40000 });
      setKit(data);
      toast.success("🚀 Building your store kit — icon, screenshots & copy land below in ~2-4 minutes.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't start the store kit.");
    } finally { setGenerating(false); }
  };

  const remove = async () => {
    if (!kit) return;
    const id = kit.id;
    setKit(null);
    try { await api.delete(`/gtm/item/${id}`); } catch { /* optimistic */ }
  };

  const shareStore = async (rotate = false) => {
    if (!kit) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/gtm/item/${kit.id}/publish`, { public: true, rotate });
      const url = `${window.location.origin}/store/${data.slug}`;
      setKit((k) => ({ ...k, public: true, slug: data.slug }));
      navigator.clipboard.writeText(url).then(
        () => toast.success(rotate ? "🔄 New link minted & copied — the old one no longer works." : "🔗 Public store link copied — share it anywhere!"),
        () => toast.success("Store page published: " + url),
      );
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't publish the store page.");
    } finally { setSharing(false); }
  };

  useEffect(() => {
    if (kit?.public) api.get(`/gtm/${gameId}/trend?days=7`).then((r) => setTrend(r.data?.trend || [])).catch(() => {});
  }, [kit?.public, kit?.id, gameId]);

  const changeTheme = async (t) => {
    if (!kit) return;
    setKit((k) => ({ ...k, theme: t }));
    try { await api.post(`/gtm/item/${kit.id}/theme`, { theme: t }); }
    catch { toast.error("Couldn't save the theme."); }
  };

  const openShare = (kind) => {
    if (!kit?.slug) return;
    const storeUrl = `${window.location.origin}/store/${kit.slug}`;
    const shareText = `Play ${kit.game_title} — made with GameView Studio!`;
    api.post(`/gtm/store/${kit.slug}/hit`, { kind: "share" }).catch(() => {});
    if (kind === "x") window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(storeUrl)}`, "_blank", "noopener");
    else if (kind === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + storeUrl)}`, "_blank", "noopener");
    else if (kind === "discord") navigator.clipboard.writeText(`${shareText} ${storeUrl}`).then(() => toast.success("Copied — paste it into Discord!"), () => toast.error("Couldn't copy the link."));
  };

  const downloadImage = (url, name) => {
    const a = document.createElement("a");
    a.href = mediaUrl(url); a.download = name; a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  const processing = kit?.status === "processing";
  const failed = kit?.status === "failed";
  const ready = kit?.status === "ready";

  const checklist = game ? [
    { key: "cover", label: "Cover image", done: !!game.cover_image, fix: `/app/publish/${gameId}` },
    { key: "description", label: "Game description", done: !!(game.description && game.description.trim()), fix: `/app/publish/${gameId}` },
    { key: "age", label: "Age rating set", done: !!game.age_rating, fix: `/app/publish/${gameId}` },
    { key: "screenshot", label: "At least one screenshot", done: (game.screenshots || []).length > 0, fix: `/app/publish/${gameId}` },
    { key: "storekit", label: "Store kit generated", done: ready, fix: null },
    { key: "published", label: "Published to arcade", done: game.status === "published", fix: `/app/publish/${gameId}` },
    { key: "trailer", label: "Trailer", done: cineCount > 0, optional: true, fix: `/app/cinematic/${gameId}` },
  ] : [];
  const reqItems = checklist.filter((c) => !c.optional);
  const pct = reqItems.length ? Math.round((reqItems.filter((c) => c.done).length / reqItems.length) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="gtm-studio">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="gtm-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><Rocket className="w-6 h-6 text-indigo-500" /> Go-to-Market Kit</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">A 1-click app-store page — icon, screenshots, copy & hashtags</p>
          </div>
        </div>
        {ready && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={shareStore} disabled={sharing} data-testid="gtm-share-btn">
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} {kit.public ? "Copy link" : "Share store page"}
            </Button>
            {kit.public && kit.slug && <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => window.open(`/store/${kit.slug}`, "_blank")} data-testid="gtm-open-store-btn"><ExternalLink className="w-4 h-4" /></Button>}
            {kit.public && <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => shareStore(true)} disabled={sharing} title="Regenerate link — the old link stops working" data-testid="gtm-rotate-btn"><RefreshCw className="w-4 h-4" /></Button>}
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => copyText(kitToMarkdown(kit), "Full kit (markdown)")} data-testid="gtm-copy-all-btn"><Copy className="w-4 h-4" /> Copy all</Button>
            <Button variant="ghost" size="sm" className="rounded-full text-[hsl(var(--muted-foreground))]" onClick={remove} data-testid="gtm-delete-btn"><Trash2 className="w-4 h-4" /> Discard</Button>
          </div>
        )}
      </div>

      {/* Publish readiness checklist */}
      {game && (
        <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-transparent p-4 mb-6" data-testid="gtm-checklist">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Ready to launch?</p>
            <span className="text-xs font-bold" data-testid="gtm-checklist-pct">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden mb-3">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <ul className="space-y-1.5">
            {checklist.map((c) => (
              <li key={c.key} className="flex items-center gap-2 text-sm" data-testid={`gtm-check-${c.key}`}>
                {c.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />}
                <span className={c.done ? "" : "text-[hsl(var(--muted-foreground))]"}>{c.label}{c.optional && <span className="text-[10px] text-[hsl(var(--muted-foreground))]"> · optional</span>}</span>
                {!c.done && c.fix && <button onClick={() => navigate(c.fix)} className="ml-auto text-xs font-semibold text-indigo-500 hover:underline shrink-0" data-testid={`gtm-fix-${c.key}`}>Fix →</button>}
              </li>
            ))}
          </ul>
          {pct === 100 && <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-3" data-testid="gtm-checklist-ready">🎉 You're launch-ready!</p>}
        </div>
      )}

      {/* Generator */}
      <div className="rounded-2xl border border-indigo-500/40 bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/5 p-4 mb-6">
        <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-2 flex items-center gap-1.5"><Store className="w-3.5 h-3.5" /> {kit ? "Regenerate your store kit" : "Generate your store kit"}</p>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">The AI writes a marketing listing and paints a bold app icon + {`3`} key-art screenshots for your game — ready to paste into any app store or share post.</p>
        <Button className="rounded-full" onClick={generate} disabled={generating || processing} data-testid="gtm-generate-btn">
          {generating || processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating || processing ? "Building…" : (kit ? "Regenerate kit" : "Generate kit")}
        </Button>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Uses 20 AI credits · AI-generated key art (not real in-engine capture). Clearly labeled AI-generated.</p>
      </div>

      {!kit && (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center" data-testid="gtm-empty">
          <Rocket className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No store kit yet — hit <b>Generate kit</b> to create a ready-to-launch marketing page for your game.</p>
        </div>
      )}

      {processing && (
        <div className="rounded-2xl border border-[hsl(var(--border))] p-10 text-center" data-testid="gtm-processing">
          <Loader2 className="w-9 h-9 mx-auto text-indigo-500 animate-spin mb-3" />
          <p className="text-sm font-semibold">Writing your listing & painting key art…</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">This takes ~2-4 minutes — the page updates automatically.</p>
        </div>
      )}

      {failed && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-8 text-center" data-testid="gtm-failed">
          <p className="text-sm text-rose-500 font-semibold">Generation failed — please try again.</p>
        </div>
      )}

      {ready && (
        <div className="space-y-6" data-testid="gtm-kit">
          {/* Hero / feature banner */}
          {kit.banner_url && (
            <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden group relative" data-testid="gtm-banner">
              <img src={mediaUrl(kit.banner_url)} alt="Store hero banner" className="w-full aspect-[16/6] object-cover" data-testid="gtm-banner-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
              <div className="absolute bottom-2.5 left-3 right-3 flex items-end justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-white/90 bg-black/40 rounded-full px-2.5 py-1 backdrop-blur">Hero banner · AI-generated</span>
                <button onClick={() => downloadImage(kit.banner_url, "store-banner.png")} className="w-9 h-9 grid place-items-center rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="gtm-banner-download"><Download className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          {/* Store page theme */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gtm-theme-picker">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Store page theme <span className="normal-case font-normal text-[10px]">· live preview</span></p>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {[...THEME_SWATCHES, {
                id: "seasonal", label: "Seasonal", seasonal: true,
                bg: "#0a0a10", accent: activeEvent?.accent || "#fb923c", emoji: activeEvent?.emoji || "🗓️",
                grad: `radial-gradient(120px 60px at 50% -10%, ${activeEvent?.accent || "#fb923c"}80, transparent)`,
              }].map((t) => {
                const active = (kit.theme || "midnight") === t.id;
                return (
                  <button key={t.id} onClick={() => changeTheme(t.id)} data-testid={`gtm-theme-${t.id}`}
                    title={t.seasonal && activeEvent?.name ? `Tracks the live event: ${activeEvent.name}` : t.label}
                    className={`rounded-xl border p-1 transition-all ${active ? "border-indigo-500 ring-2 ring-indigo-500/40" : "border-[hsl(var(--border))] hover:border-indigo-400/60"}`}>
                    <div className="rounded-lg overflow-hidden w-full h-16 flex flex-col justify-between p-1.5" style={{ background: `${t.grad}, ${t.bg}` }}>
                      <div className="flex items-center gap-1">
                        {t.seasonal ? <span className="w-4 h-4 grid place-items-center text-[11px] leading-none">{t.emoji}</span> : (kit.icon_url ? <img src={mediaUrl(kit.icon_url)} alt="" className="w-4 h-4 rounded" /> : <span className="w-4 h-4 rounded" style={{ background: t.accent }} />)}
                        <span className="h-1.5 w-7 rounded-full bg-white/40" />
                      </div>
                      <div className="space-y-0.5">
                        <span className="block h-1 w-10 rounded-full bg-white/20" />
                        <span className="block h-1 w-6 rounded-full bg-white/20" />
                      </div>
                      <span className="inline-block h-2.5 w-8 rounded-full" style={{ background: t.accent }} />
                    </div>
                    <div className="flex items-center justify-center gap-1 text-[11px] font-semibold mt-1">
                      {active && <CheckCircle2 className="w-3 h-3 text-indigo-500" />} {t.label}
                    </div>
                  </button>
                );
              })}
            </div>
            {(kit.theme || "midnight") === "seasonal" && <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2">{activeEvent ? `Auto-matches the live event — currently ${activeEvent.emoji} ${activeEvent.name}.` : "Auto-matches the live in-app event (falls back to Midnight when no event is running)."}</p>}
          </div>

          {/* Share to socials */}
          {kit.public && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gtm-socials">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Share to socials</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => openShare("x")} data-testid="gtm-share-x" className="rounded-full px-3.5 py-1.5 text-xs font-bold bg-black text-white hover:opacity-90 transition-opacity">𝕏  Share</button>
                <button onClick={() => openShare("whatsapp")} data-testid="gtm-share-whatsapp" className="rounded-full px-3.5 py-1.5 text-xs font-bold text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: "#25D366" }}>WhatsApp</button>
                <button onClick={() => openShare("discord")} data-testid="gtm-share-discord" className="rounded-full px-3.5 py-1.5 text-xs font-bold text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: "#5865F2" }}>Discord</button>
              </div>
            </div>
          )}

          {/* Store performance */}
          {kit.public && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gtm-stats">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">Store performance</p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { key: "views", label: "Views", icon: Eye, v: kit.stats?.views || 0 },
                  { key: "plays", label: "Plays", icon: Gamepad2, v: kit.stats?.plays || 0 },
                  { key: "scans", label: "QR scans", icon: QrCode, v: kit.stats?.scans || 0 },
                  { key: "shares", label: "Shares", icon: Share2, v: kit.stats?.shares || 0 },
                ].map((s) => (
                  <div key={s.key} className="rounded-xl bg-[hsl(var(--secondary))] p-3 text-center" data-testid={`gtm-stat-${s.key}`}>
                    <s.icon className="w-4 h-4 mx-auto text-indigo-500 mb-1" />
                    <div className="text-lg font-bold tabular-nums leading-none" data-testid={`gtm-stat-${s.key}-value`}>{s.v}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
              {trend.length > 0 && (
                <div className="mt-4" data-testid="gtm-trend">
                  <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Last 7 days · views</p>
                  <div className="flex items-end gap-1.5 h-20">
                    {trend.map((d) => {
                      const max = Math.max(1, ...trend.map((t) => t.views));
                      const h = Math.max(4, Math.round((d.views / max) * 100));
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`gtm-trend-${d.date}`} title={`${d.date}: ${d.views} views · ${d.plays} plays · ${d.scans} scans · ${d.shares} shares`}>
                          <div className="w-full flex items-end justify-center h-16">
                            <div className="w-full rounded-t bg-indigo-500/70 transition-all" style={{ height: `${h}%` }} />
                          </div>
                          <span className="text-[9px] text-[hsl(var(--muted-foreground))]">{new Date(d.date).toLocaleDateString(undefined, { weekday: "short" }).slice(0, 2)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Embed on your site */}
          {kit.public && kit.slug && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gtm-embed">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Code2 className="w-3.5 h-3.5" /> Embed on your site</p>
                <a href={`/embed/store/${kit.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-indigo-500 hover:underline" data-testid="gtm-embed-preview">Preview →</a>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Paste this into any blog, itch.io page or docs — a live "Play now" card.</p>
              <div className="flex items-start gap-2">
                <textarea readOnly value={`<iframe src="${window.location.origin}/embed/store/${kit.slug}" width="100%" height="160" style="border:0;border-radius:16px;max-width:520px" loading="lazy" title="${kit.game_title}"></iframe>`}
                  onFocus={(e) => e.target.select()} data-testid="gtm-embed-code"
                  className="flex-1 h-20 text-[10px] font-mono rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-2 resize-none" />
                <Button size="sm" className="rounded-full shrink-0" onClick={() => copyText(`<iframe src="${window.location.origin}/embed/store/${kit.slug}" width="100%" height="160" style="border:0;border-radius:16px;max-width:520px" loading="lazy" title="${kit.game_title}"></iframe>`, "Embed code")} data-testid="gtm-embed-copy"><Copy className="w-4 h-4" /> Copy</Button>
              </div>
            </div>
          )}

          {/* Header card: icon + tagline + category */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 flex items-start gap-5 flex-wrap">
            {kit.icon_url && (
              <div className="relative group">
                <img src={mediaUrl(kit.icon_url)} alt="App icon" className="w-28 h-28 rounded-3xl object-cover shadow-lg" data-testid="gtm-icon" />
                <button onClick={() => downloadImage(kit.icon_url, "app-icon.png")} className="absolute inset-0 grid place-items-center rounded-3xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white" data-testid="gtm-icon-download"><Download className="w-6 h-6" /></button>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="font-display text-xl font-bold truncate">{kit.game_title}</h2>
                {kit.category && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 px-2.5 py-0.5 text-[11px] font-bold" data-testid="gtm-category"><Tag className="w-3 h-3" /> {kit.category}</span>}
              </div>
              {kit.tagline && <p className="text-base font-semibold text-[hsl(var(--foreground))]" data-testid="gtm-tagline">{kit.tagline}</p>}
              {kit.short_desc && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1" data-testid="gtm-short-desc">{kit.short_desc}</p>}
            </div>
          </div>

          {/* Screenshots */}
          {(kit.screenshots || []).some((s) => s.image_url) && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Screenshots</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="gtm-screenshots">
                {kit.screenshots.map((s, i) => s.image_url && (
                  <div key={i} className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden group relative" data-testid={`gtm-screenshot-${i}`}>
                    <img src={mediaUrl(s.image_url)} alt={s.caption || `Screenshot ${i + 1}`} className="w-full aspect-[3/4] object-cover" />
                    {s.caption && <p className="p-2 text-xs font-semibold truncate">{s.caption}</p>}
                    <button onClick={() => downloadImage(s.image_url, `screenshot-${i + 1}.png`)} className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gtm-screenshot-download-${i}`}><Download className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {kit.long_desc && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="gtm-long-desc-card">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Description</p>
                <Button variant="ghost" size="sm" className="h-7 rounded-full text-xs" onClick={() => copyText(kit.long_desc, "Description")} data-testid="gtm-copy-desc-btn"><Copy className="w-3.5 h-3.5" /> Copy</Button>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="gtm-long-desc">{kit.long_desc}</p>
            </div>
          )}

          {/* Features */}
          {(kit.features || []).length > 0 && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="gtm-features-card">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Features</p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {kit.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`gtm-feature-${i}`}>
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Hashtags */}
          {(kit.hashtags || []).length > 0 && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="gtm-hashtags-card">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> Hashtags</p>
                <Button variant="ghost" size="sm" className="h-7 rounded-full text-xs" onClick={() => copyText(kit.hashtags.map((h) => `#${h}`).join(" "), "Hashtags")} data-testid="gtm-copy-hashtags-btn"><Copy className="w-3.5 h-3.5" /> Copy</Button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="gtm-hashtags">
                {kit.hashtags.map((h, i) => (
                  <span key={i} className="rounded-full bg-[hsl(var(--secondary))] px-3 py-1 text-xs font-semibold" data-testid={`gtm-hashtag-${i}`}>#{h}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
